/**
 * Desktop chip-strip + typed-prefix input for one stocktake line's
 * count_expression. Replaces the bare numeric ``RowCountInput`` on the
 * desktop branch of the stocktake detail page.
 *
 * Per the spec's "Desktop Count Entry — Chip Strip + Typed Prefix
 * Grammar" requirement:
 *
 *   - Single text input per row, hardened against browser autocomplete +
 *     numeric spinners + dead-key noise (`type="text"`, `autocomplete="off"`).
 *   - Grammar (Enter commits one term):
 *       N or +N            → loose
 *       xN / XN / ×N       → crates (requires pack_size)
 *       -N (Unicode minus) → broken
 *   - Chips render left-to-right in commit order. Each non-sealed chip
 *     has a tiny × that pops just that term.
 *   - Running total `= N` rendered whenever the term list is non-empty.
 *
 * The component is uncontrolled w.r.t. the input value but controlled
 * w.r.t. the term list: ``onCommit`` is called with the new term list
 * + counted_qty after a successful parse or chip removal. The parent
 * owns the PATCH dispatch (so the same PATCH queue + If-Match
 * concurrency model from the existing RowCountInput applies).
 */

import { Input, Tag, Tooltip, Typography } from "antd";
import { useState, type KeyboardEvent } from "react";

import {
  invariantViolation,
  isSealed,
  parseTypedInput,
  stripSealed,
  sumSigned,
  type Term,
} from "../../utils/countExpression";

const { Text } = Typography;

const POSITIVE_TAG_COLOR = "green";
const BROKEN_TAG_COLOR = "red";

interface Props {
  /** Current committed term list, or null/empty per the synthesis rule. */
  terms: Term[] | null;
  /** Article's current pack_size; null disables the ``xN`` grammar. */
  packSize: number | null;
  /** Disabled flag — proxied from the row-level edit gate (in-progress
   * vs reverted vs offline). */
  disabled?: boolean;
  /** Called with (nextTerms, nextCountedQty) after every commit / chip-
   * removal. Parent persists via PATCH and updates the row state. */
  onCommit: (terms: Term[], countedQty: number) => void;
  /** Fired true on input focus, false on blur. Lets the parent keep a
   * just-counted row visible under "Hide counted" until the operator
   * leaves the field (instead of hiding it the instant Enter commits). */
  onFocusChange?: (focused: boolean) => void;
}

function termTagLabel(term: Term): string {
  if (term.kind === "crates") {
    return `+${term.n}×${term.pack_size}`;
  }
  if (term.kind === "broken") {
    return `−${term.n}`;
  }
  return `+${term.n}`;
}

function termColor(term: Term): string {
  return term.kind === "broken" ? BROKEN_TAG_COLOR : POSITIVE_TAG_COLOR;
}

export const CountExpressionInline = ({
  terms,
  packSize,
  disabled,
  onCommit,
  onFocusChange,
}: Props) => {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const liveTerms: Term[] = terms ?? [];
  const liveSealed = isSealed(liveTerms);
  const total = liveTerms.length > 0 ? sumSigned(liveTerms) : 0;
  // `terms === []` (an explicit empty array) means "counted as zero";
  // `null` means uncounted. Show "= 0" for the former so the operator can
  // tell the line is counted at zero rather than blank/untouched.
  const countedAsZero = Array.isArray(terms) && terms.length === 0;

  const handleCommit = () => {
    const trimmed = draft.trim();
    if (trimmed === "") return;
    // Explicit "counted as zero" (stock legitimately depleted): a lone 0
    // with no other terms commits an EMPTY expression at counted_qty 0.
    // The grammar forbids n=0 TERMS, but a LINE can be counted as zero —
    // distinct from the uncounted state. Skips the parser's zero rejection.
    if (/^\+?0+$/.test(trimmed) && stripSealed(liveTerms).length === 0) {
      setError(null);
      setDraft("");
      onCommit([], 0);
      return;
    }
    const result = parseTypedInput(draft, packSize);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const newTerm = result.term;
    // Adding a new term implicitly unseals the legacy term — strip the
    // flag before persisting, per the spec's "Adding a new term unseals
    // the legacy term" scenario.
    const nextTerms = [...stripSealed(liveTerms), newTerm];
    const violation = invariantViolation(
      nextTerms,
      sumSigned(nextTerms),
    );
    if (violation !== null) {
      setError(violation);
      return;
    }
    setError(null);
    setDraft("");
    onCommit(nextTerms, sumSigned(nextTerms));
  };

  const handleRemove = (idx: number) => {
    if (disabled) return;
    if (liveTerms[idx]?.sealed === true) return;
    const nextTerms = stripSealed(
      liveTerms.filter((_, i) => i !== idx),
    );
    onCommit(nextTerms, sumSigned(nextTerms));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCommit();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
    >
      {/* Chip strip */}
      {liveTerms.map((t, i) => {
        const label = termTagLabel(t);
        const sealed = t.sealed === true;
        const tag = (
          <Tag
            key={`${i}-${label}`}
            color={termColor(t)}
            closable={!sealed && !disabled}
            onClose={(ev) => {
              ev?.preventDefault();
              handleRemove(i);
            }}
            style={{
              margin: 0,
              opacity: sealed ? 0.55 : 1,
              fontFamily: "var(--font-mono, monospace)",
            }}
          >
            {label}
          </Tag>
        );
        if (sealed) {
          return (
            <Tooltip
              key={`${i}-${label}-tip`}
              title="legacy count — add a new entry first to edit"
            >
              {tag}
            </Tooltip>
          );
        }
        return tag;
      })}

      {/* Typed-prefix input */}
      <Input
        size="small"
        type="text"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        placeholder={packSize ? `N · xN · −N` : `N · −N`}
        value={draft}
        disabled={disabled}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error !== null) setError(null);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => {
          handleCommit();
          onFocusChange?.(false);
        }}
        style={{ width: 80, fontFamily: "var(--font-mono, monospace)" }}
      />

      {/* Running total. Always rendered when there's at least one term
          so even a single `5×20` shows `= 100` per the spec's locked
          decision (always-on, not only when >=2). */}
      {(liveTerms.length > 0 || countedAsZero) && (
        <Text strong style={{ marginLeft: 4 }}>
          = {total}
        </Text>
      )}

      {/* Inline parse error — clears on next keystroke. */}
      {error !== null && (
        <Tag color="default" style={{ margin: 0 }}>
          ↯ {error}
        </Tag>
      )}

      {/* Suppress unused-warning for liveSealed; future revisions might
          use it for an icon/badge. */}
      {liveSealed && null}
    </div>
  );
};
